import { Rss } from 'lucide-react'
import { useBooks } from '../hooks/useBooks'
import type { Book } from '../types'
import './SubscriberFeedCard.css'

interface SubscriberFeedCardProps {
  book: Book
}

const KNOWN_TICK_KEYS = ['source_account_id', 'as_of', 'accepted', 'entries', 'holds', 'exits']

/**
 * Honest wiring check: shows what source book this validation book
 * subscribes to, and whether the mirror feed has actually ticked. A
 * last_conviction_tick of null means no tick since the last restart (the
 * report is in-memory, so a routine deploy clears it) — that's expected,
 * not a halt or an error, and it repopulates on the next daily tick.
 */
export function SubscriberFeedCard({ book }: SubscriberFeedCardProps) {
  const { byId } = useBooks()
  const sourceId = book.pick_source_account_id
  const source = sourceId !== null ? byId(sourceId) : undefined
  const tick = book.bookStatus?.last_conviction_tick ?? null

  return (
    <div className="card subscriber-feed-card">
      <div className="card-header">
        <h3 className="card-title">
          <Rss size={18} className="icon" aria-hidden="true" />
          Subscriber feed
        </h3>
      </div>

      {sourceId === null ? (
        <div className="empty-state">
          <div className="empty-icon"><Rss size={28} aria-hidden="true" /></div>
          <div className="empty-text">no pick source configured</div>
        </div>
      ) : (
        <>
          <div className="subscriber-feed-source">
            <span className="caption">Subscribes to</span>
            <span className="subscriber-feed-arrow" aria-hidden="true">→</span>
            <span className="subscriber-feed-source-label">
              {source?.label ?? (sourceId !== null ? `Account ${sourceId}` : 'unknown')}
            </span>
            <span className="mono subscriber-feed-source-id">#{sourceId}</span>
          </div>

          {tick === null ? (
            <div className="subscriber-feed-status subscriber-feed-status--inactive">
              no tick since restart · awaiting next daily tick
            </div>
          ) : (
            <div className="subscriber-feed-status subscriber-feed-status--active">
              <div className="subscriber-feed-status-label">feed active · last tick</div>
              <dl className="subscriber-feed-tick mono">
                {KNOWN_TICK_KEYS.filter((k) => k in tick).map((k) => (
                  <div key={k} className="subscriber-feed-tick-row">
                    <dt>{k}</dt>
                    <dd>{JSON.stringify(tick[k])}</dd>
                  </div>
                ))}
                {Object.keys(tick).filter((k) => !KNOWN_TICK_KEYS.includes(k)).map((k) => (
                  <div key={k} className="subscriber-feed-tick-row">
                    <dt>{k}</dt>
                    <dd>{JSON.stringify(tick[k])}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </>
      )}
    </div>
  )
}
